import React, { useState, useEffect } from 'react';
import { phoneNumbersAPI } from '../lib/api';
import { PhoneIncoming, PhoneOutgoing, Phone, Loader2, Play, Pause, Download } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatTimestampCompact } from '../lib/timezone';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatPhone(number) {
  if (!number) return '';
  const d = number.replace(/[^\d]/g, '');
  if (d.length === 11 && d[0] === '1') {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return number;
}

const STATUS_STYLES = {
  completed: 'text-emerald-600 dark:text-emerald-400',
  connected: 'text-blue-600 dark:text-blue-400',
  ringing: 'text-amber-600 dark:text-amber-400',
  missed: 'text-red-600 dark:text-red-400',
  rejected: 'text-zinc-500 dark:text-zinc-400',
};

function RecordingControls({ callId }) {
  const [playing, setPlaying] = useState(false);
  const [audioRef] = useState(() => ({ current: null }));

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      audioRef.current = new Audio(phoneNumbersAPI.getRecordingUrl(callId));
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => {
        toast.error('Failed to load recording');
        setPlaying(false);
      };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = phoneNumbersAPI.getRecordingUrl(callId, true);
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex items-center gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={togglePlay}
        title={playing ? 'Pause' : 'Play recording'}
        data-testid={`play-recording-${callId}`}
      >
        {playing
          ? <Pause className="h-3.5 w-3.5" />
          : <Play className="h-3.5 w-3.5" />}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={handleDownload}
        title="Download recording"
        data-testid={`download-recording-${callId}`}
      >
        <Download className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function CustomerCallsTab({ customerId }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    phoneNumbersAPI.callHistory({ customer_id: customerId, page, page_size: pageSize })
      .then(res => {
        setCalls(res.data.calls || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => setCalls([]))
      .finally(() => setLoading(false));
  }, [customerId, page]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="calls-tab-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="calls-tab-empty">
        <Phone className="w-8 h-8 mb-2 opacity-40" />
        <p className="text-sm">No call history yet</p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-1" data-testid="calls-tab">
      {calls.map(call => {
        const isInbound = call.direction === 'inbound';
        const displayPhone = isInbound ? call.from_number : call.to_number;

        return (
          <div
            key={call.id}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
            data-testid={`call-row-${call.id}`}
          >
            <div className={cn(
              'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
              isInbound ? 'bg-blue-50 dark:bg-blue-950' : 'bg-emerald-50 dark:bg-emerald-950'
            )}>
              {isInbound
                ? <PhoneIncoming className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                : <PhoneOutgoing className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">
                  {isInbound ? 'Inbound Call' : 'Outbound Call'}
                </span>
                <span className={cn('text-xs capitalize', STATUS_STYLES[call.status] || 'text-zinc-500')}>
                  {call.status}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{formatPhone(displayPhone)}</p>
            </div>
            {/* Recording controls */}
            {call.has_recording && <RecordingControls callId={call.id} />}
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-mono tabular-nums">{formatDuration(call.duration_seconds)}</p>
              <p className="text-xs text-muted-foreground">{formatTimestampCompact(call.started_at)}</p>
            </div>
          </div>
        );
      })}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-3">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 text-xs rounded border disabled:opacity-30 hover:bg-muted"
          >
            Prev
          </button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 text-xs rounded border disabled:opacity-30 hover:bg-muted"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
